import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Button } from 'dibs-elements/exports/Button';
import { moreCreatorDesigns } from '../../shared/sharedMessages';
import { useViewMore } from '../hooks/viewMore/useViewMore';
import { viewMore, viewLess } from '../sbMessages';
import { SbSharedCreatorDesignsTile } from './SbSharedCreatorDesignsTile';
import classNames from 'classnames';

import styles from './SbRespCreatorDesigns.scss';

import { type SbRespCreatorDesigns_designPageInfo$key } from './__generated__/SbRespCreatorDesigns_designPageInfo.graphql';
import { type Currency } from './__generated__/SbSharedCreatorDesignsTile_creatorDesigns.graphql';

type Props = {
    designPageInfo: SbRespCreatorDesigns_designPageInfo$key;
    currency: Currency;
};

const REVEAL_COUNT = 4;
const INITIAL_COUNT = 4;

export const SbRespCreatorDesigns: FC<Props> = ({
    designPageInfo: designPageInfoRef,
    currency,
}) => {
    const designPageInfo = useFragment(
        graphql`
            fragment SbRespCreatorDesigns_designPageInfo on DesignPageInfo {
                design {
                    displayName
                }
                primaryCreator {
                    displayName
                }
                creatorDesigns {
                    url
                    browsePageLinkData {
                        isLinkable
                    }
                    ...SbSharedCreatorDesignsTile_creatorDesigns
                }
            }
        `,
        designPageInfoRef
    );

    const creatorDesigns = designPageInfo?.creatorDesigns || [];
    const creatorDesignsCount = creatorDesigns.length;
    const primaryCreator = designPageInfo?.primaryCreator;
    const hasLinkableDesign = creatorDesigns.some(design => design?.browsePageLinkData?.isLinkable);

    const { viewMoreEl, dispatch, visibleCount } = useViewMore<HTMLButtonElement>({
        totalCount: creatorDesignsCount,
        revealCount: REVEAL_COUNT,
        initialCount: INITIAL_COUNT,
    });

    if (!primaryCreator || !creatorDesignsCount || !hasLinkableDesign) {
        return null;
    }

    return (
        <div className={styles.container} data-tn="more-designs-container">
            <div className={styles.header} data-tn="more-designs-header">
                {moreCreatorDesigns(primaryCreator?.displayName)}
            </div>
            <div
                className={classNames(styles.designs, {
                    [styles.justifyCenter]: creatorDesignsCount < INITIAL_COUNT,
                })}
            >
                {creatorDesigns.map(
                    (design, idx) =>
                        design &&
                        idx < visibleCount && (
                            <SbSharedCreatorDesignsTile
                                key={design?.url}
                                iconicDesign={design}
                                currency={currency}
                            />
                        )
                )}
            </div>
            {creatorDesignsCount > INITIAL_COUNT && (
                <div className={styles.viewMoreButtonDiv}>
                    <Button
                        buttonRef={viewMoreEl}
                        type="secondary"
                        size="medium"
                        dataTn="related-sellers-view-more"
                        onClick={e => {
                            e.currentTarget.blur();
                            dispatch();
                        }}
                        className={styles.viewMoreButton}
                    >
                        {visibleCount < creatorDesignsCount ? viewMore : viewLess}
                    </Button>
                </div>
            )}
        </div>
    );
};
